import {
  Badge,
  type BadgeProps,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  type StackProps,
} from "@chakra-ui/react";
import {
  type SimpleTaskFragment,
  type TaskPriority,
  TaskSystemPriorityEnum,
} from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import type { IconNames } from "@src/utils/types/iconNames";
import type { FunctionComponent } from "react";

const PRIORITY_ICON_NAME: Record<TaskSystemPriorityEnum, IconNames> = {
  [TaskSystemPriorityEnum.Low]: "chevron-down",
  [TaskSystemPriorityEnum.Medium]: "equal",
  [TaskSystemPriorityEnum.High]: "chevron-up",
  [TaskSystemPriorityEnum.Urgent]: "alert-square",
};

const PRIORITY_COLOR_SCHEME: Record<
  TaskSystemPriorityEnum,
  BadgeProps["colorScheme"]
> = {
  [TaskSystemPriorityEnum.Low]: "gray",
  [TaskSystemPriorityEnum.Medium]: "teal",
  [TaskSystemPriorityEnum.High]: "orange",
  [TaskSystemPriorityEnum.Urgent]: "red",
};

type TaskPriorityBadgeProps = {
  priority: SimpleTaskFragment["priority"];
  // TODO: This is stupid as hell, update to this visual in all places
  asBadge?: boolean;
} & Omit<StackProps, "onChange"> &
  (
    | {
        isEditable: boolean;
        taskPriorities: TaskPriority[] | undefined;
        onChange: (priorityId: TaskPriority["id"]) => void;
        isLoading?: boolean;
      }
    | {
        isEditable?: never;
        taskPriorities?: never;
        onChange?: never;
        isLoading?: never;
      }
  );

export const TaskPriorityBadge: FunctionComponent<TaskPriorityBadgeProps> = ({
  priority,
  isEditable,
  taskPriorities,
  onChange,
  isLoading,
  asBadge = false,
  ...props
}) => {
  return (
    <WrapComponent
      if={isEditable ?? false}
      with={(children) => (
        <Menu isLazy>
          <MenuButton onClick={(e) => e.stopPropagation()}>
            {isLoading ? <Spinner color="teal.500" size="xs" /> : children}
          </MenuButton>
          <Portal>
            <MenuList>
              {taskPriorities?.map(({ name, id }) => (
                <MenuItem
                  key={name}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange?.(id);
                  }}
                >
                  {name}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      )}
    >
      {asBadge ? (
        <Badge
          colorScheme={
            priority.system_priority
              ? PRIORITY_COLOR_SCHEME[priority.system_priority]
              : "gray"
          }
          rounded="base"
          size="sm"
        >
          {priority.system_priority && (
            <Icon
              boxSize="4"
              name={PRIORITY_ICON_NAME[priority.system_priority]}
              mr="2"
              color="inherit"
            />
          )}
          {priority.name}
        </Badge>
      ) : (
        <HStack
          flex="0"
          color="gray.500"
          fontFamily="heading"
          fontSize="xs"
          fontWeight="semibold"
          whiteSpace="nowrap"
          spacing="2"
          {...props}
        >
          {priority.system_priority && (
            <Icon
              w="5"
              h="5"
              color="grey.500"
              name={PRIORITY_ICON_NAME[priority.system_priority]}
            />
          )}
          <span>{priority.name}</span>
        </HStack>
      )}
    </WrapComponent>
  );
};
